import { useEffect, useState } from "react";
import { Box, Typography, LinearProgress, alpha, useTheme } from "@mui/material";
import { ACCENT } from "./accent";

/**
 * What the backend is doing while a log parses.
 *
 * The API reports `pending`/`parsing` and nothing finer — no percentage — so
 * this shows an indeterminate bar and names the stage the parse is most likely
 * in, advancing on elapsed time. A real percentage would have to be invented,
 * and a progress bar that lies about how far along it is costs more trust than
 * it buys patience.
 */

/** Rough stage timings, in seconds since the parse started. */
const STAGES = [
  { after: 0, message: "Reading the flight log…" },
  { after: 6, message: "Extracting the flight summary…" },
  { after: 14, message: "Checking vibration and EKF health…" },
  { after: 24, message: "Reviewing GPS quality and battery…" },
  { after: 34, message: "Running filter, PID and compass analysis…" },
  { after: 48, message: "Assembling the report…" },
];

const stageFor = (elapsedS) =>
  [...STAGES].reverse().find((s) => elapsedS >= s.after)?.message ?? STAGES[0].message;

const ParseProgress = ({ log }) => {
  const theme = useTheme();
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, []);

  const queued = log.status === "pending";

  return (
    <Box sx={{ maxWidth: 460, mx: "auto", py: 6, textAlign: "center" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, color: theme.palette.text.primary }}>
        Analysing your flight log
      </Typography>
      <Typography
        variant="body2"
        sx={{ mt: 0.5, mb: 3, color: theme.palette.text.secondary, wordBreak: "break-word" }}
      >
        {queued ? "Queued — waiting for a parser…" : stageFor(elapsed)}
      </Typography>

      <LinearProgress
        sx={{
          height: 6,
          borderRadius: 3,
          backgroundColor: alpha(theme.palette.text.primary, 0.08),
          "& .MuiLinearProgress-bar": {
            borderRadius: 3,
            backgroundImage: `linear-gradient(90deg, ${ACCENT}, ${alpha(ACCENT, 0.45)})`,
          },
        }}
      />

      <Typography
        variant="caption"
        sx={{ display: "block", mt: 1.5, fontFamily: "monospace", color: theme.palette.text.secondary }}
      >
        {log.file_name} · {String(Math.floor(elapsed / 60)).padStart(2, "0")}:
        {String(elapsed % 60).padStart(2, "0")} elapsed
      </Typography>
    </Box>
  );
};

export default ParseProgress;
